// Reports the steps of a Hanko flow to GTM, for the screens that drive one: the login widget
// (hanko_login.js) and the authenticator app setup screen (hanko_mfa_setup.js).
//
// Each step the user sees pushes `hanko_view_<step>` when it loads and `hanko_submit_<step>` when
// they submit it, and a step that comes back with an error pushes `hanko_flow_error`. Every event
// carries the flow, state and action it came from.

// Hanko flow states we report to GTM, mapped to the step name used in the
// event. Hanko has more states than these, but the ones left out are stepped
// through automatically (preflight, thirdparty, passkey attestation, ...) and
// never appear to the user as a screen, so counting them would inflate the
// funnel.
const TRACKED_FLOW_STATES = {
  login_init: "login_email",
  registration_init: "registration_email",
  passcode_confirmation: "verify_email_code",
  login_password: "enter_password",
  password_creation: "create_password",
  login_password_recovery: "recover_password",
  credential_onboarding_chooser: "choose_credential",
  onboarding_create_passkey: "create_passkey",
  mfa_method_chooser: "choose_mfa_method",
  mfa_otp_secret_creation: "setup_authenticator_app",
  mfa_security_key_creation: "setup_security_key",
  login_otp: "enter_mfa_code",
  login_security_key: "use_security_key",
  device_trust: "trust_device",
  success: "auth_success"
};

// The Hanko SDK dispatches its flow events on `document`, not on the element,
// so these listeners are global and must only be registered once even if more
// than one widget is mounted.
let flowTrackingRegistered = false;

export function pushFlowEvent(eventName, state) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    hankoFlow: state.flowName,
    hankoState: state.name,
    hankoAction: state.invokedAction?.name ?? state.previousAction?.name ?? null
  });
}

export function registerFlowTracking(hanko) {
  if (flowTrackingRegistered) return;
  flowTrackingRegistered = true;

  // Fires when the user submits an action from the step they're on, giving us
  // the attempt half of each funnel step.
  hanko.onBeforeStateChange(({ state }) => {
    const step = TRACKED_FLOW_STATES[state.name];
    if (step) pushFlowEvent(`hanko_submit_${step}`, state);
  });

  // Fires once the next step has loaded, giving us the arrival half. States
  // restored from localStorage (`isCached`) are replays of a step the user has
  // already reached - a page refresh mid-flow - so they're skipped.
  hanko.onAfterStateChange(({ state }) => {
    if (state.isCached) return;

    const step = TRACKED_FLOW_STATES[state.name];
    if (step) pushFlowEvent(`hanko_view_${step}`, state);

    // Hanko 3.0 declares a `hanko-flow-error` event but never dispatches it;
    // failures surface as an `error` on the state we land on instead.
    if (state.error) pushFlowEvent("hanko_flow_error", state);
  });
}
